import { getCrmColors } from "../../theme/crmColors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import { Edit as EditIcon, DeleteOutline as DeleteOutlineIcon } from "@material-ui/icons";
import TableRowSkeleton from "../TableRowSkeleton";

const useStyles = makeStyles((theme) => {
  const isDark = Boolean(theme.replyDark);

  return {
    tableContainer: {
      borderRadius: 8,
      backgroundColor: isDark ? getCrmColors(theme, "dark").surface : "#ffffff",
      ...(isDark && { border: `1px solid ${getCrmColors(theme, "dark").border}` }),
      boxShadow: isDark
        ? "0 14px 34px rgba(0, 0, 0, 0.24)"
        : "0 12px 30px rgba(19, 27, 46, 0.06)",
      [theme.breakpoints.down("sm")]: {
        overflowX: "visible",
      },
    },
    table: {
      minWidth: 760,
      borderCollapse: "separate",
      borderSpacing: "0 4px",
      [theme.breakpoints.down("sm")]: {
        minWidth: 0,
        display: "block",
        "& thead": {
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
        },
        "& tbody": { display: "block", padding: theme.spacing(1) },
        "& tbody > tr:not($row):not($emptyRow)": {
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          "& td": { display: "block", padding: theme.spacing(1) },
        },
      },
    },
    headerCell: {
      borderBottom: "none",
      color: isDark ? "#FFFFFF" : "#4c5d8d",
      backgroundColor: isDark ? getCrmColors(theme, "dark").surface : "#ffffff",
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    row: {
      backgroundColor: isDark ? getCrmColors(theme, "dark").inset : "#ffffff",
      transition: "background-color 180ms ease",
      "&:nth-of-type(even)": {
        backgroundColor: isDark ? getCrmColors(theme, "dark").surface : "#f7f9ff",
      },
      "&:hover": {
        backgroundColor: isDark ? getCrmColors(theme, "dark").hover : "#eef4ff",
      },
      "& td": {
        borderBottom: "none",
        color: isDark ? "#FFFFFF" : "inherit",
        paddingTop: theme.spacing(1.35),
        paddingBottom: theme.spacing(1.35),
      },
      [theme.breakpoints.down("sm")]: {
        display: "block",
        marginBottom: theme.spacing(1),
        borderRadius: 8,
        border: `1px solid ${isDark ? getCrmColors(theme, "dark").border : "#d9e2ef"}`,
        "&:last-child": { marginBottom: 0 },
        "& td": {
          display: "flex",
          alignItems: "flex-start",
          gap: theme.spacing(1),
          width: "100%",
          maxWidth: "none",
          padding: theme.spacing(1.25),
          textAlign: "left",
          "&::before": {
            content: "attr(data-label)",
            flex: "0 0 64px",
            paddingTop: 3,
            color: isDark ? getCrmColors(theme, "dark").muted : "#737685",
            fontSize: 11,
            fontWeight: 700,
          },
        },
      },
    },
    keywordChip: {
      maxWidth: 220,
      color: isDark ? "#FFFFFF" : "#0040a2",
      backgroundColor: isDark ? getCrmColors(theme, "dark").accentSoft : "#dae2ff",
      fontWeight: 800,
      "& .MuiChip-label": {
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      [theme.breakpoints.down("sm")]: {
        maxWidth: "calc(100% - 72px)",
      },
    },
    conditionChip: {
      color: isDark ? "#FFFFFF" : "#415382",
      backgroundColor: isDark ? getCrmColors(theme, "dark").raised : "#e2e7ff",
      fontWeight: 700,
      [theme.breakpoints.down("sm")]: {
        maxWidth: "calc(100% - 72px)",
      },
    },
    responseCell: {
      maxWidth: 560,
    },
    responsePreview: {
      display: "-webkit-box",
      overflow: "hidden",
      color: isDark ? "#FFFFFF" : "#434654",
      lineHeight: 1.45,
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      [theme.breakpoints.down("sm")]: {
        flex: 1,
        minWidth: 0,
        overflowWrap: "anywhere",
        WebkitLineClamp: 3,
      },
    },
    emptyRow: {
      [theme.breakpoints.down("sm")]: { display: "block" },
    },
    emptyCell: {
      borderBottom: "none",
      padding: theme.spacing(6, 2),
      [theme.breakpoints.down("sm")]: { display: "block" },
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      color: isDark ? getCrmColors(theme, "dark").muted : "#737685",
      textAlign: "center",
    },
    emptyTitle: {
      color: isDark ? "#FFFFFF" : "#131b2e",
      fontSize: 16,
      fontWeight: 800,
    },
    emptyText: {
      maxWidth: 420,
      fontSize: 13,
      lineHeight: 1.5,
    },
    actionButton: {
      width: 34,
      height: 34,
      margin: theme.spacing(0, 0.25),
      backgroundColor: isDark ? getCrmColors(theme, "dark").surface : "#ffffff",
      boxShadow: isDark
        ? "0 6px 16px rgba(0, 0, 0, 0.2)"
        : "0 6px 16px rgba(19, 27, 46, 0.08)",
      [theme.breakpoints.down("sm")]: {
        width: 40,
        height: 40,
        flexShrink: 0,
      },
    },
    editButton: {
      color: isDark ? "#FFFFFF" : "#0052cc",
      "&:hover": {
        backgroundColor: isDark ? getCrmColors(theme, "dark").hover : "#dae2ff",
      },
    },
    deleteButton: {
      color: isDark ? "#fca5a5" : "#ba1a1a",
      "&:hover": {
        backgroundColor: isDark ? "rgba(239, 68, 68, 0.16)" : "#ffdad6",
      },
    },
  };
});

const conditionLabels = {
  equal: "Igual",
  contains: "Contem",
  startsWith: "Comeca com",
  endsWith: "Termina com",
};

function AutoRepliesTable({ records, showLoading, editRecord, deleteRecord }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (Array.isArray(records)) {
      setRows(records);
    }
    if (showLoading !== undefined) {
      setLoading(showLoading);
    }
  }, [records, showLoading]);

  const renderRows = () => {
    if (!rows.length) {
      return (
        <TableRow className={classes.emptyRow}>
          <TableCell colSpan={4} className={classes.emptyCell}>
            <Box className={classes.emptyState}>
              <Box className={classes.emptyTitle}>Nenhuma resposta encontrada</Box>
              <Box className={classes.emptyText}>
                Crie uma resposta automatica ou ajuste a busca para encontrar os atalhos cadastrados.
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((record) => {
      const condition = conditionLabels[record.condition] || record.condition || "Sem condicao";
      const keyword = record.keyword || "Sem palavra";
      const response = record.response || "Sem resposta cadastrada";

      return (
        <TableRow key={record.id} className={classes.row}>
          <TableCell align="center" data-label="Palavra">
            <Chip size="small" label={keyword} className={classes.keywordChip} />
            {record.active === false && <Chip size="small" label="Pausada" variant="outlined" />}
          </TableCell>
          <TableCell align="center" data-label="Condição">
            <Chip size="small" label={condition} className={classes.conditionChip} />
          </TableCell>
          <TableCell align="left" data-label="Resposta" className={classes.responseCell}>
            <span className={classes.responsePreview} title={response}>
              {response}
            </span>
          </TableCell>
          <TableCell align="center" data-label="Ações">
            <Tooltip title="Editar resposta">
              <IconButton
                size="small"
                onClick={() => editRecord(record)}
                className={`${classes.actionButton} ${classes.editButton}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir resposta">
              <IconButton
                size="small"
                onClick={() => deleteRecord(record)}
                className={`${classes.actionButton} ${classes.deleteButton}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <TableContainer className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell align="center" className={classes.headerCell}>Palavra</TableCell>
            <TableCell align="center" className={classes.headerCell}>Condicao</TableCell>
            <TableCell align="left" className={classes.headerCell}>Resposta</TableCell>
            <TableCell align="center" className={classes.headerCell}>Acoes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? <TableRowSkeleton columns={4} /> : renderRows()}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

AutoRepliesTable.propTypes = {
  records: PropTypes.array.isRequired,
  showLoading: PropTypes.bool,
  editRecord: PropTypes.func.isRequired,
  deleteRecord: PropTypes.func.isRequired,
};

export default AutoRepliesTable;
